/**
 * window.__e2e bridge (LainGram).
 *
 * Activated ONLY when the page is loaded with `?e2e=1` — zero cost and zero
 * surface in normal use, but the production bundle stays controllable by
 * Playwright. The bridge observes state through the same DOM the markers
 * expose; it never mutates app state (tests drive the real UI).
 */
import appDialogsManager from '@lib/appDialogsManager';
import {flushClientLogs, getClientLogSessionTag} from '@lib/debug/mountLogShip';

export type E2eBridge = {
  sessionTag: string
  state(): 'booting' | 'auth' | 'im'
  ready(): Promise<'auth' | 'im'>
  imReady(): Promise<void>
  dialogPeerIds(): number[]
  hasDialogFor(peerId: number): boolean
  waitFor(predicate: () => boolean | Promise<boolean>, timeoutMs?: number): Promise<void>
  flushClientLogs(): Promise<void>
};

const POLL_INTERVAL_MS = 100;
const AUTH_MARKER = '[data-testid^="auth:"]';

function hasDialogList() {
  try {
    return !!appDialogsManager.chatList?.querySelector('[data-peer-id]');
  } catch(err) {
    // chatList's getter touches IM-boot state that does not exist yet.
    return false;
  }
}

function currentState(): 'booting' | 'auth' | 'im' {
  if(hasDialogList()) return 'im';
  if(document.querySelector(AUTH_MARKER)) return 'auth';
  return 'booting';
}

export function activateE2eBridge() {
  if((window as any).__e2e) return;

  const poll = async<T>(probe: () => T | undefined, timeoutMs: number): Promise<T> => {
    const deadline = Date.now() + timeoutMs;
    for(;;) {
      const value = probe();
      if(value !== undefined) return value;
      if(Date.now() >= deadline) throw new Error(`__e2e poll timed out after ${timeoutMs}ms`);
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
  };

  const bridge: E2eBridge = {
    sessionTag: getClientLogSessionTag(),
    state: currentState,
    ready: () => poll(() => {
      const s = currentState();
      return s === 'booting' ? undefined : s;
    }, 60_000),
    imReady: async() => { await poll(() => hasDialogList() ? true : undefined, 60_000); },
    dialogPeerIds: () => {
      try {
        return Array.from(appDialogsManager.chatList?.querySelectorAll<HTMLElement>('[data-peer-id]') || [])
          .map((row) => Number(row.dataset.peerId))
          .filter((id) => !!id);
      } catch(err) {
        return [];
      }
    },
    hasDialogFor: (peerId) => {
      try {
        return !!appDialogsManager.chatList?.querySelector(`[data-peer-id="${peerId}"]`);
      } catch(err) {
        return false;
      }
    },
    waitFor: async(predicate, timeoutMs = 15_000) => {
      await poll(() => {
        try {
          return predicate() ? true : undefined;
        } catch(err) {
          return undefined;
        }
      }, timeoutMs);
    },
    flushClientLogs: () => flushClientLogs()
  };

  document.documentElement.dataset.e2eSession = bridge.sessionTag;
  (window as any).__e2e = bridge;
}
